import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { PrtgInstanceStatus } from "src/questionnaires";

export interface UsePrtgStatusResult {
  status: PrtgInstanceStatus | null;
  loading: boolean;
  error: string | null;
  /** "" when the appliance has no `common.metadata.name` to scope PRTG by. */
  instanceName: string;
  refetch: () => Promise<void>;
}

/**
 * Fetch the current PRTG snapshot for a single appliance instance.
 *
 * Shared by the flyout health view and the per-appliance monitoring tab. Never
 * throws: 503 (not configured) / 502 (PRTG unreachable) all degrade to an
 * `error` string with `status = null` so callers can render an honest
 * "unavailable" / "unmonitored" surface instead of crashing.
 *
 * Pass `enabled = false` to defer the network call (e.g. fan-out gating).
 */
export const usePrtgStatus = (
  instanceName: string,
  enabled: boolean = true,
): UsePrtgStatusResult => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();

  const [status, setStatus] = useState<PrtgInstanceStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string",
    [keycloak],
  );

  const trimmedName = instanceName?.trim() ?? "";

  const refetch = useCallback(async () => {
    if (!enabled || !canFetch || trimmedName === "") return;
    setLoading(true);
    setError(null);
    try {
      const path = `v2/${AnketaResourcesEnum.opsanio}/prtg/instance/${encodeURIComponent(
        trimmedName,
      )}`;
      const { success, data, error: respError } = await Get<{
        status: number;
        data: PrtgInstanceStatus;
      }>({
        path,
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || respError) {
        setStatus(null);
        const debugError = (respError as any)?.service?.debugError;
        setError(debugError ?? respError?.message ?? "PRTG_UNAVAILABLE");
      } else {
        setStatus(data?.data ?? null);
      }
    } catch (err) {
      setStatus(null);
      setError("PRTG_UNAVAILABLE");
    } finally {
      setLoading(false);
    }
    // `Get` is intentionally excluded: useApi() returns a fresh Get on every
    // render, so including it would make refetch (and the effect below) re-run
    // every render → infinite fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, canFetch, trimmedName, keycloak]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { status, loading, error, instanceName: trimmedName, refetch };
};
